import { useCallback, useEffect, useState } from 'react'
import { Button, Descriptions, Divider, Form, Input, InputNumber, Modal, Select, Space, TableProps } from 'antd'
import { EyeOutlined, PlusOutlined, MinusOutlined, EditOutlined } from '@ant-design/icons'
import CustomTable from '../../components/table'
import defaultParams from '../../assets/constants/default_params'
import { toastError, toastSuccess } from '../../utils/toast'
import { useLoading } from '../../context/LoadingContext'
import { Link } from 'react-router-dom'
import { DETAIL, IMPORT, ORDER } from '../../assets/constants/route_path'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { orderStatusOptions } from '../../assets/constants/default_options'
import FormSearch from '../../components/form'
import { apiGetListCompanies, apiGetListOrderWarehouse, apiAddOrderWarehouse, apiGetSimInventoryByOrderIds, updateOrderWarehouse } from '../../api'
import { ICompany } from '../../models/company.modal'
import { renderOrderStatus } from '../quanlytopup'
import { useSelector } from 'react-redux'
import { authSelector } from '../../redux/slices/authSlice'
import moment from 'moment'

const initOrderInfo = { cpCode: "", description: "" }
const initSimInventory = { id: 0, startSim: "", endSim: "", disabled: false, prefix_sim: "", start_sim: 0, end_sim: 0, total_sim: 0 }

export default function SimImportOrder() {
    const [companyOptions, setCompanyOptions] = useState<{ label: string, value: Number }[]>([])
    const [orderWarehouse, setOrderWarehouse] = useState<any>({})
    const [listSimByOrderIds, setListSimByOrderIds] = useState<any>([])
    const [currentPage, setCurrentPage] = useState<number>(defaultParams.PAGE)
    const [pageSize, setPageSize] = useState<number>(defaultParams.LIMIT)
    const [search, setSearch] = useState<any>({})
    const [modalUpload, setModalUpload] = useState<boolean>(false)
    const [orderInfo, setOrderInfo] = useState(initOrderInfo)
    const [simInventory, setSimInventory] = useState([initSimInventory])
    const [modalUpdate, setModalUpdate] = useState<{ isOpen: boolean, data: any }>({ isOpen: false, data: null })

    const { setIsLoading } = useLoading()
    const [formSearch] = Form.useForm()
    const [formUpdate] = Form.useForm()
    const { account } = useSelector(authSelector)

    const columns: TableProps<any>['columns'] = [
        {
            key: 'order_id',
            title: 'Mã đơn hàng',
            dataIndex: 'order_id',
            render: order_id => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {order_id}
                </span>
            ),
        },
        {
            key: 'owner_by',
            title: 'Nhân sự tạo',
            dataIndex: 'owner_by',
        },
        {
            key: 'description',
            title: 'Nội dung',
            dataIndex: 'description',
        },
        {
            key: 'cpCode',
            title: 'Mã công ty',
            dataIndex: 'cpCode',
        },
        {
            key: 'totalSim',
            title: 'Tổng số sim',
            dataIndex: 'total_sim',
        },
        {
            key: 'total_success',
            title: 'Tổng thành công',
            dataIndex: 'total_success',
        },
        {
            key: 'total_fail',
            title: 'Tổng thất bại',
            dataIndex: 'total_fail',
        },
        {
            key: 'created_at',
            title: 'Ngày nhập',
            dataIndex: 'created_at',
            render: created_at => moment(created_at).format('DD/MM/YYYY HH:mm:ss'),
        },
        {
            key: 'simInventory',
            title: 'Đã xuất',
            dataIndex: 'simInventory',
            render: simInventory => simInventory?.reduce((acc: number, curr: any) => acc + curr.total_active, 0),
        },
        {
            key: 'status',
            title: 'Trạng thái',
            dataIndex: 'status',
            render: status => renderOrderStatus(status),
        },
        {
            key: 'action',
            title: 'Tác vụ',
            dataIndex: 'action',
            align: 'center',
            render: (_, data: any) => (
                <Space>
                    {account?.canGetListSimInventory && (
                        <Link to={IMPORT + ORDER + DETAIL + `?orderId=${data.order_id}`}>
                            <Button
                                icon={<EyeOutlined />}
                                style={{ background: '#289e45', color: 'white' }}
                            />
                        </Link>
                    )}
                    {account?.canUpdateOrderWarehouse && (
                        <Button
                            onClick={() => handleOpenModalUpdate(data)}
                            icon={<EditOutlined />}
                            style={{ background: '#ff9800', color: 'white' }}
                        />
                    )}
                </Space>
            ),
        },
    ]

    const fetchData = useCallback(async () => {
        try {
            setIsLoading(true)
            const { data } = await apiGetListOrderWarehouse({
                cpCode: search.cpCode,
                status: search.status,
                page: currentPage,
                size: pageSize,
                orderBy: 'created_at desc',
            })
            setOrderWarehouse(data)
            mapData(data?.orderWarehouse)
        } catch (error) {
            console.log(error)
        } finally {
            setIsLoading(false)
        }
    }, [setIsLoading, currentPage, pageSize, search])

    const mapData = async (data: any[]) => {
        try {
            if (!data || data.length === 0) return
            const orderIds = data.map((item: any) => item.order_id)
            const resp = await apiGetSimInventoryByOrderIds({ orderIds })
            const simInventoryData = resp.data.simInventory
            setOrderWarehouse((prev: any) => ({
                ...prev,
                orderWarehouse: data.map((item: any) => ({
                    ...item,
                    simInventory: simInventoryData.filter((sim: any) => sim.order_id === item.order_id),
                }))
            }))
            setListSimByOrderIds(simInventoryData)
        } catch (error) {
            console.log(error)
        }
    }

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const fetchCompanies = useCallback(async () => {
        try {
            const { data } = await apiGetListCompanies({ limit: defaultParams.MAX_LIMIT })
            const options = data.companies?.filter((item: ICompany) => item.supportChannels?.includes('WAREHOUSE')).map((item: ICompany) => ({
                label: item.name,
                value: item.code
            }))
            setCompanyOptions(options)
        } catch (error) {
            console.log(error)
        }
    }, [])

    useEffect(() => {
        fetchCompanies()
    }, [fetchCompanies])

    const handleSearch = ({ cpCode, status }: any) => {
        if (cpCode || status) {
            setSearch({ cpCode, status })
            setCurrentPage(defaultParams.PAGE)
            setPageSize(defaultParams.LIMIT)
        }
    }

    const handleClearSearch = () => {
        formSearch.resetFields()
        setSearch({})
        setCurrentPage(defaultParams.PAGE)
        setPageSize(defaultParams.LIMIT)
    }

    const handleChange = (id: any, key: any, value: any) => {
        setSimInventory((prev) => prev.map((cond) => (cond.id === id ? { ...cond, [key]: value } : cond)))
    }

    const validateSimInventory = () => {
        const isValid = simInventory.every((item) => {
            return (item.startSim?.length >= 19 && /^\d+$/.test(item.startSim))
                && (item.endSim?.length >= 19 && /^\d+$/.test(item.endSim))
                && (item.startSim?.length === item.endSim?.length)
                && item.startSim?.slice(0, -5) === item.endSim?.slice(0, -5)
                && Number(item.startSim?.slice(-5)) <= Number(item.endSim?.slice(-5))
        })
        if (!isValid) {
            toastError('Serial Sim không hợp lệ')
            return false
        }
        const lastSim = simInventory[simInventory.length - 1]
        lastSim.prefix_sim = lastSim.startSim.slice(0, 14)
        lastSim.start_sim = Number(lastSim.startSim.slice(-5))
        lastSim.end_sim = Number(lastSim.endSim.slice(-5))
        for (let i = 0; i < simInventory.length - 1; i++) {
            if (simInventory[i].prefix_sim === lastSim.prefix_sim
                && Number(simInventory[i].start_sim) <= Number(lastSim.end_sim)
                && Number(lastSim.start_sim) <= Number(simInventory[i].end_sim)
            ) {
                toastError('Serial Sim không được trùng nhau')
                return false
            }
        }
        return true
    }

    const handleAddSimInventory = () => {
        if (!validateSimInventory()) return
        setSimInventory((prev) => [...prev.map((item) => {
            item.disabled = true
            item.prefix_sim = item.startSim.slice(0, 14)
            item.start_sim = Number(item.startSim.slice(-5))
            item.end_sim = Number(item.endSim.slice(-5))
            item.total_sim = item.end_sim - item.start_sim + 1
            return item
        }), { ...initSimInventory, id: simInventory.length + 1 }])
    }

    const handleAddOrderWarehouse = async () => {
        try {
            setIsLoading(true)
            if (!orderInfo.cpCode || !orderInfo.description) {
                toastError('Vui lòng nhập đầy đủ thông tin')
                return
            }
            if (!validateSimInventory()) return
            await apiAddOrderWarehouse({
                cpCode: orderInfo.cpCode,
                description: orderInfo.description,
                simInventory,
            })
            fetchData()
            handleCloseModalAddOrderWarehouse()
            toastSuccess('nạp sim đang thực hiện! Vui lòng kiểm tra kết quả sau ít phút')
        } catch (error) {
            console.log(error);
            toastError('Có lỗi xảy ra!')
        } finally {
            setIsLoading(false)
        }
    }

    const handleCloseModalAddOrderWarehouse = () => {
        setModalUpload(false)
        setOrderInfo(initOrderInfo)
        setSimInventory([initSimInventory])
    }

    const handleOpenModalUpdate = (data: any) => {
        formUpdate.setFieldsValue(data)
        setModalUpdate({ isOpen: true, data })
    }

    const handleCloseModalUpdate = () => {
        setModalUpdate({ isOpen: false, data: null })
        formUpdate.resetFields()
    }

    const handleUpdateOrderWarehouse = async (values: any) => {
        try {
            setIsLoading(true)
            await updateOrderWarehouse({ orderWarehouse: values })
            toastSuccess('Cập nhật đơn hàng thành công!')
            fetchData()
            handleCloseModalUpdate()
        } catch (error) {
            console.log(error)
            toastError('Có lỗi xảy ra khi cập nhật!')
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <FormSearch form={formSearch} handleSearch={handleSearch} handleClearSearch={handleClearSearch}>
                <Form.Item name='cpCode' label='Công ty'>
                    <Select
                        style={{ minWidth: 170 }}
                        showSearch
                        placeholder="Chọn công ty"
                        options={companyOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
                <Form.Item name='status' label='Trạng thái'>
                    <Select
                        style={{ width: 170 }}
                        showSearch
                        placeholder="Chọn trạng thái"
                        options={orderStatusOptions}
                        filterOption={filterOptionByLabel}
                    />
                </Form.Item>
            </FormSearch>
            {account?.canAddOrderWarehouse && (
                <div style={{ textAlign: 'end', marginBottom: 20 }}>
                    <Button onClick={() => setModalUpload(true)} type='primary'>Thêm đơn hàng</Button>
                </div>
            )}
            <Descriptions
                style={{ maxWidth: 600 }}
                items={[
                    {
                        key: 'totalOrder',
                        label: 'Tổng đơn hiện thị',
                        children: orderWarehouse.orderWarehouse?.length || 0,
                    },
                    {
                        key: 'totalSuccess',
                        label: 'Tổng thành công',
                        children: orderWarehouse.orderWarehouse?.reduce((acc: number, curr: any) => acc + curr.total_success, 0) || 0,
                    },
                    {
                        key: 'totalExport',
                        label: 'Tổng đã xuất',
                        children: listSimByOrderIds?.reduce((acc: number, curr: any) => acc + curr.total_active, 0) || 0,
                    },
                ]}
            />
            <CustomTable
                namePage='đơn hàng'
                columns={columns}
                dataSource={orderWarehouse.orderWarehouse}
                pageSize={pageSize}
                setPageSize={setPageSize}
                total={orderWarehouse.total}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
            />
            <Modal title='Thêm đơn hàng' open={modalUpload}
                onCancel={handleCloseModalAddOrderWarehouse}
                footer={[
                    <Button onClick={handleAddOrderWarehouse}
                        style={{ background: '#008965', color: 'white' }}>
                        Xác nhận
                    </Button>,
                    <Button onClick={handleCloseModalAddOrderWarehouse}
                        style={{ background: 'gray', color: 'white' }}>
                        Đóng
                    </Button>,
                ]}>
                <Form layout="vertical" autoComplete="off" style={{ marginBottom: 40 }}>
                    <Form.Item label='Công ty'>
                        <Select
                            style={{ maxWidth: 200 }}
                            value={orderInfo.cpCode}
                            onChange={value => setOrderInfo({ ...orderInfo, cpCode: value })}
                            showSearch
                            placeholder="Chọn công ty"
                            options={companyOptions}
                            filterOption={filterOptionByLabel}
                        />
                    </Form.Item>
                    {simInventory.map((sim) => (
                        <div key={sim.id} style={{ display: 'flex', gap: 20 }}>
                            <Form.Item style={{ flex: 1 }} label='Serial bắt đầu'>
                                <Input
                                    disabled={sim.disabled}
                                    value={sim.startSim}
                                    onChange={e => handleChange(sim.id, "startSim", e.target.value)}
                                />
                            </Form.Item>
                            <Form.Item style={{ flex: 1 }} label='Serial kết thúc'>
                                <Input
                                    disabled={sim.disabled}
                                    value={sim.endSim}
                                    onChange={e => handleChange(sim.id, "endSim", e.target.value)}
                                />
                            </Form.Item>
                            {sim.total_sim > 0 && (
                                <Form.Item label=' '>
                                    <b>{sim.total_sim}</b>
                                </Form.Item>
                            )}
                            <Form.Item label=' '>
                                <Button
                                    onClick={handleAddSimInventory}
                                    icon={<PlusOutlined />} size='small'
                                />
                            </Form.Item>
                            <Form.Item label=' '>
                                {simInventory.length > 1 && (
                                    <Button
                                        onClick={() => setSimInventory((prev) => prev.filter((item) => item.id !== sim.id))}
                                        icon={<MinusOutlined />} danger size='small'
                                    />
                                )}
                            </Form.Item>
                        </div>
                    ))}
                    <Form.Item style={{ flex: 1 }} label='Mô tả'>
                        <Input.TextArea
                            value={orderInfo.description}
                            onChange={(e) => setOrderInfo({ ...orderInfo, description: e.target.value })}
                        />
                    </Form.Item>
                </Form>
                <p><b>Ghi chú:</b></p>
                <p>14 số đầu của dãy Serial phải trùng nhau</p>
                <p><b>Ví dụ:</b></p>
                <p>Serial bắt đầu: 8984048000900700000</p>
                <p>Serial kết thúc: 8984048000900799999</p>
                <Divider />
            </Modal>
            <Modal
                title='Cập nhật đơn hàng'
                open={modalUpdate.isOpen}
                onCancel={handleCloseModalUpdate}
                footer={[]}
                width={600}
            >
                <Form
                    form={formUpdate}
                    onFinish={handleUpdateOrderWarehouse}
                    layout="vertical"
                    autoComplete="off"
                    style={{ marginTop: 20 }}
                >
                    <Form.Item name='id' style={{ display: 'none' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item label='Mã đơn hàng' style={{ flex: 1 }}>
                            <Input
                                value={modalUpdate.data?.order_id}
                                disabled
                            />
                        </Form.Item>
                        <Form.Item label='Nhân sự' style={{ flex: 1 }}>
                            <Input
                                value={modalUpdate.data?.owner_by}
                                disabled
                            />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='cpCode' label='Công ty' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn công ty"
                                options={companyOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                        <Form.Item name='status' label='Trạng thái' style={{ flex: 1 }}>
                            <Select
                                showSearch
                                placeholder="Chọn trạng thái"
                                options={orderStatusOptions}
                                filterOption={filterOptionByLabel}
                            />
                        </Form.Item>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}>
                        <Form.Item name='total_sim' label='Tổng Sim' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='total_success' label='Tổng thành công' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                        <Form.Item name='total_fail' label='Tổng thất bại' style={{ flex: 1 }}>
                            <InputNumber style={{ width: '100%' }} />
                        </Form.Item>
                    </div>
                    <Form.Item name='description' label='Mô tả'>
                        <Input.TextArea />
                    </Form.Item>
                    <div style={{ textAlign: 'right' }}>
                        <Button
                            htmlType='submit'
                            style={{ background: '#ff9800', color: 'white' }}>
                            Cập nhật
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    )
}
